import { Select, Spin, message } from "antd";
import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import {
  APIError,
  Department,
  FormFieldConfig,
  Role,
  User,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";

import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";
import BaseForm from "../components/Form/BaseForm";
import debounce from "lodash.debounce";
import { ALL_ROLES_QUERY } from "../gql/roles";
import { GET_USER, UPDATE_USER } from "../gql/users";
import { GET_DEPARTMENTS } from "../gql/departments";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  id: string | number;
  name: string;
  email: string;
  departments: Department[];
  department: Department;
  departmentId: string;
  roleId: string;
  roles: Role[];
  role: Role;
  isLoading: boolean;
  search: string;
}

interface RouteParams {
  id: string;
}
const { Option } = Select;

export function SelectOptionFactory(
  data: Role[] | Department[],
): JSX.Element[] {
  return data.map((item: Role | Department): JSX.Element => {
    return (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    );
  });
}


export default class EditUser extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      departments: [],
      roles: [],
      name: "",
      email: "",
      role: {
        id: "",
        name: "",
        users: [],
      },
      department: {
        id: "",
        name: "",
        users: [],
      },
      isLoading: true,
      departmentId: "",
      roleId: "",
      id: "",
      search: "",
    };

    this.reference = React.createRef<FormInstance>();

    this.redirectToUserPage = this.redirectToUserPage.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.getUserData = this.getUserData.bind(this);
    this.getDepartmentsAndRoles = this.getDepartmentsAndRoles.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.updateUser = this.updateUser.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async getUserData(id: string): Promise<void> {
    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: GET_USER,
        variables: { id },
      });

      if (!data.errors) {
        this.setState({
          name: data.data.user.name,
          email: data.data.user.email,
          departmentId: data.data.user.department.id,
          roleId: data.data.user.role.id,
          department: data.data.user.department,
          role: data.data.user.role,
          isLoading: false,
        });
      } else {
        message.error("Failed to get user data");
        this.setState({ isLoading: false });
      }
    } catch (error) {
      message.error(`Failed to get user data - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  }

  getAllDepartments = (): Promise<AxiosResponse> => {
    return request({ query: GET_DEPARTMENTS });
  };

  getAllRoles = (): Promise<AxiosResponse> => {
    return request({ query: ALL_ROLES_QUERY });
  };

  async getDepartmentsAndRoles(): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const [roles, departments]: any = await Promise.all([
        this.getAllRoles(),
        this.getAllDepartments(),
      ]);
      this.setState({
        isLoading: false,
        roles: roles.data.data.roles.results,
        departments: departments.data.data.departments,
      });
    } catch (error) {
      message.error(`Failed to load departments and roles - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  handleSearchInput = async (search: string): Promise<void> => {
    this.setState({ search });
    await this.triggerDelaySearch();
  };

  triggerDelaySearch = debounce(async (): Promise<void> => {
    const newRoles = await this.getAllRoles();
    this.setState({
      roles: newRoles.data.data.roles.results ?? [],
    });
  }, DEBOUNCE_TIME);

  handleInputChange(
    key: string,
  ): (e: React.ChangeEvent<HTMLInputElement>) => void {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      this.setState({ ...this.state, [key.toString()]: e.target.value });
    };
  }

  handleSelectChange(key: string): (value: string) => void {
    return (value): void => {
      this.setState({
        ...this.state,
        [key.toString()]: value,
      });
    };
  }

  async updateUser(): Promise<void> {
    const { id } = this.state;

    interface Response {
      data?: {
        createUser: {
          user: User;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "name",
        "email",
        "departmentId",
        "roleId",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_USER,
        variables: { id, ...data },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateUser",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("User updated sucessfully");
        this.redirectToUserPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update user - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  redirectToUserPage(): void {
    this.props.history.push(`/users/${this.state.id}`);
  }

  async componentDidMount(): Promise<void> {
    const userId = this.props.match.params.id;
    this.setState({ id: userId });
    await Promise.all([this.getDepartmentsAndRoles(), this.getUserData(userId)]);
  }

  render(): JSX.Element | null {
    const { isLoading, departments, roles, name, email, department, role } =
      this.state;

    if (!name) return <Spin spinning={true}></Spin>;
    const fields: FormFieldConfig[] = [
      {
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        initialValue: name,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "email",
        rule: "email",
        inputType: "email",
        initialValue: email,
        required: true,
        label: "Email",
        placeholder: "Email",
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: true,
        initialValue: department.id,
        label: "Department",
        placeholder: "Department",
        selectOptions: departments,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "roleId",
        rule: "string",
        inputType: "search",
        initialValue: role.id,
        required: true,
        label: "Role",
        placeholder: "Role",
        selectOptions: roles,
        changeHandler: this.handleSearchInput,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "updateUserButton",
        name: "updateUserButton",
        inputType: "button",
        value: "Update",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateUser,
      },
    ];

    return (
      <Card header="">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
